// frontend/src/hooks/useProfileStats.ts

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/authStore';
import { ProfileStats, AIUsageEntry, RecentBadge } from '../types/profile.types';

export function useProfileStats() {
  const userId = useAuthStore((s) => s.session?.user.id);
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [aiHistory, setAiHistory] = useState<AIUsageEntry[]>([]);
  const [recentBadges, setRecentBadges] = useState<RecentBadge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const [lessonsRes, quizzesRes, projectsRes, badgesRes, aiMessagesRes] = await Promise.all([
          supabase
            .from('student_course_progress')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          supabase
            .from('quiz_attempts')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          supabase
            .from('submissions')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          supabase
            .from('student_achievements')
            .select('earned_at, achievements(icon, title)')
            .eq('student_id', userId)
            .order('earned_at', { ascending: false })
            .limit(8),
          supabase
            .from('ai_messages')
            .select('id, content, created_at, sender')
            .eq('student_id', userId)
            .eq('sender', 'user')
            .order('created_at', { ascending: false })
            .limit(4),
        ]);

        if (cancelled) return;

        setStats({
          completedLessons: lessonsRes.count ?? 0,
          totalQuizzes: quizzesRes.count ?? 0,
          completedProjects: projectsRes.count ?? 0,
          earnedBadges: (badgesRes.data ?? []).length,
        });

        setRecentBadges(
          (badgesRes.data ?? []).map((b: any) => ({
            icon: b.achievements?.icon ?? '🏆',
            title: b.achievements?.title ?? 'Başarı Rozeti',
          }))
        );

        if (aiMessagesRes.data && aiMessagesRes.data.length > 0) {
          setAiHistory(
            aiMessagesRes.data.map((m: any) => ({
              id: m.id,
              title: m.content?.length > 40 ? m.content.substring(0, 40) + '...' : (m.content || 'AI Mentor Sorusu'),
              type: 'chat',
              occurredAt: m.created_at,
            }))
          );
        } else {
          setAiHistory([]);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Profil verisi yüklenemedi.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { stats, aiHistory, recentBadges, isLoading, error };
}
